import { useEffect, useState } from "react";

import { PayinQrCard } from "@/modules/dashboard/payins/components/PayinQrCard";
import { pixQrDataUrl } from "@/modules/dashboard/payins/utils/pixQrDataUrl";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import type { BrebReceivingKey } from "@/shared/services/payments/brebReceivingKey.service";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import { BrebKeyListeningNote, BrebKeyPaymentsReceived } from "./BrebKeyPaymentsReceived";
import { useBrebKeyIncomingPayments } from "./useBrebKeyIncomingPayments";
import { useBrebKeyRangeLabel } from "./useBrebKeyRangeLabel";

interface RenderedQr {
  payload: string;
  src: string | null;
}

export interface BrebKeyStaticQrProps {
  receivingKey: BrebReceivingKey;
  /** Staff only: the organization the key belongs to, so its payments are watched there. */
  organizationId?: string;
  className?: string;
}

/**
 * The key's any-amount QR, drawn from its own payload, or a placeholder while the backend is still minting
 * it. Nothing for a key that cannot be paid yet. While the QR is on screen, every payment that reaches the
 * key is announced above it, so whoever shows the QR knows the money arrived without asking the payer.
 */
export function BrebKeyStaticQr({ receivingKey, organizationId, className }: BrebKeyStaticQrProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const range = useBrebKeyRangeLabel(receivingKey);
  const payload = receivingKey.status === "PENDING" ? null : receivingKey.static_qr_payload;
  const generating = receivingKey.status === "ACTIVE" && !receivingKey.static_qr_payload;
  const [rendered, setRendered] = useState<RenderedQr | null>(null);
  const { payments, arrivals, lastArrival } = useBrebKeyIncomingPayments(
    payload ? receivingKey : null,
    organizationId,
  );

  useEffect(() => {
    if (!payload) return;
    let cancelled = false;
    void pixQrDataUrl(payload).then((src) => {
      if (!cancelled) setRendered({ payload, src });
    });
    return () => {
      cancelled = true;
    };
  }, [payload]);

  if (payload) {
    return (
      <div className={className}>
        {/* Mounted before anything arrives, so a screen reader announces the first payment too. A new node per
            arrival, so two payments of the same amount are both read out. */}
        <p role="status" className="sr-only">
          {lastArrival && (
            <span key={arrivals}>
              {t("breb_key_payment_received_announce", {
                amount: `${formatCurrencyAmount(lastArrival.grossAmount, "COP", activeLanguage)} COP`,
              })}
            </span>
          )}
        </p>
        <BrebKeyPaymentsReceived payments={payments} arrivals={arrivals} className="mb-3" />
        <PayinQrCard
          src={rendered?.payload === payload ? rendered.src : null}
          rail="BREB"
          footer={range}
          shareExpiry={null}
          brebKey={receivingKey.key_value}
        />
        <BrebKeyListeningNote hasPayments={payments.length > 0} className="mt-3" />
      </div>
    );
  }

  if (generating) {
    return (
      <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4">
        <ColocaSkeleton className="size-16 shrink-0 rounded-xl" />
        <p role="status" className="text-sm text-muted-foreground">
          {t("breb_key_static_qr_generating")}
        </p>
      </div>
    );
  }

  return null;
}
